import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Two round buttons that float over a table's left and right edges while it can scroll that way.
 *
 * See the note above `visibleScrollbarStyles` in `table.tsx` for why a clickable control is back
 * after #4955 took the old chevrons away. In short: a scrollbar says the table scrolls, and these say
 * where to — over the rows the reader is already looking at, rather than in a strip underneath them.
 *
 * They are drawn into MRT's paper rather than beside the table, because the paper is the one box
 * that holds the table container in both of its homes: the page, and the fixed full-screen overlay
 * MRT turns the same paper into. Positioned against it, the buttons follow the table into full screen
 * without being told.
 */

export type ScrollDirection = "left" | "right";

/** A horizontal extent in viewport pixels — a column's, or the part of the container the rows show in. */
export type HorizontalEdges = { left: number; right: number };

/** The smallest step a button takes, so a sliver of a band still moves the table visibly. */
const MINIMUM_SCROLL_STEP = 48;
/**
 * How much of the band a step lined up on a column edge must still cover. Below it the edge is too
 * close to be worth lining up on, and the step takes the whole band instead.
 */
const MINIMUM_PAGE_SHARE = 0.5;
/** Scroll positions within a pixel of the end count as the end: zoomed displays report fractions. */
const END_TOLERANCE = 1;
/** How far each button sits in from the edge it floats over, and from any pinned column there. */
const EDGE_GAP = 8;
/** Above every layer MRT draws inside the container: pinned cells, the sticky header, its drop zone. */
const ABOVE_THE_TABLE = 5;

/** Whether a scroll container's scroll offset has anywhere left to go in each direction. */
export function horizontalScrollState(container: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}): Record<ScrollDirection, boolean> {
  const furthest = container.scrollWidth - container.clientWidth;
  return {
    left: container.scrollLeft > END_TOLERANCE,
    right: container.scrollLeft < furthest - END_TOLERANCE,
  };
}

/**
 * Whether there is more of the table to see each way: the scroll offset has room to move, AND the
 * table itself runs past the edge of what the container shows.
 *
 * The offset alone is not enough, and Chromium is the case that shows it. With the container's
 * `scrollbar-gutter: stable` and no vertical scrollbar, `clientWidth` leaves the empty gutter out
 * while the scrollport keeps it, so `scrollWidth - clientWidth` claims 10px more than the table ever
 * scrolls. Measured on a draft's fee schedule in a 1100px window: `scrollLeft` stopped at 252 of a
 * computed 262, with the table's right edge and the pinned Actions column both at the container's
 * right edge, and the right button stayed up at the end with nothing left to show. The table's own
 * edges are what the reader sees, so they have the last word.
 */
export function scrollableDirections(
  offsets: Record<ScrollDirection, boolean>,
  table: HorizontalEdges | null,
  visible: HorizontalEdges,
): Record<ScrollDirection, boolean> {
  if (!table) return offsets;
  return {
    left: offsets.left && table.left < visible.left - END_TOLERANCE,
    right: offsets.right && table.right > visible.right + END_TOLERANCE,
  };
}

/**
 * How far one press scrolls: about the width of the band the scrolling columns show in — a page of
 * the table, the way Page Down is a page of a document. A column at a time made a wide fee schedule
 * a long walk of presses.
 *
 * Where it can, the step stops on a column edge rather than mid-column. Scrolling right, the column
 * cut off at the band's right edge moves to its left edge, so it opens whole; scrolling left, the
 * column cut off at the left edge moves to the right edge. That is a little short of the band, and
 * it never passes content the reader has not seen. When that edge would leave less than half a band
 * to step — a wide column that starts near the band's far edge — the step is the whole band.
 */
export function pageScrollDistance(
  direction: ScrollDirection,
  columns: readonly HorizontalEdges[],
  band: HorizontalEdges,
): number {
  const bandWidth = band.right - band.left;
  const cutOff =
    direction === "right"
      ? columns.find((column) => column.right > band.right + END_TOLERANCE)
      : [...columns].reverse().find((column) => column.left < band.left - END_TOLERANCE);
  const toColumnEdge = cutOff ? (direction === "right" ? cutOff.left - band.left : band.right - cutOff.right) : bandWidth;
  const distance = toColumnEdge >= bandWidth * MINIMUM_PAGE_SHARE ? Math.min(toColumnEdge, bandWidth) : bandWidth;
  return Math.max(MINIMUM_SCROLL_STEP, distance);
}

type ScrollButtonsLayout = {
  paper: HTMLElement;
  /** From the paper's top edge to the middle of the part of the table that is on screen. */
  top: number;
  /** From the paper's left edge to where the left button starts. */
  left: number;
  /** From the paper's right edge to where the right button ends. */
  right: number;
  canScroll: Record<ScrollDirection, boolean>;
};

function sameLayout(a: ScrollButtonsLayout | null, b: ScrollButtonsLayout | null) {
  if (a === null || b === null) return a === b;
  return (
    a.paper === b.paper &&
    a.top === b.top &&
    a.left === b.left &&
    a.right === b.right &&
    a.canScroll.left === b.canScroll.left &&
    a.canScroll.right === b.canScroll.right
  );
}

function headerCellEdges<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  headers: ReturnType<MRT_TableInstance<TData>["getCenterLeafHeaders"]>,
): HorizontalEdges[] {
  const cells = table.refs.tableHeadCellRefs.current ?? {};
  return headers.flatMap((header) => {
    const cell = cells[header.column.id];
    if (!cell) return [];
    const rect = cell.getBoundingClientRect();
    return [{ left: rect.left, right: rect.right }];
  });
}

/**
 * The part of the container that shows anything: its box less its borders, and less a vertical
 * scrollbar only while one is drawn — a reserved but empty gutter is still scrollport (see
 * `scrollableDirections`).
 */
function visibleEdges(container: HTMLElement): HorizontalEdges {
  const rect = container.getBoundingClientRect();
  const left = rect.left + container.clientLeft;
  const hasVerticalScrollbar = container.scrollHeight > container.clientHeight + END_TOLERANCE;
  return {
    left,
    right: hasVerticalScrollbar ? left + container.clientWidth : rect.right - container.clientLeft,
  };
}

/**
 * The part of the container the scrolling columns show in: what it shows, less any pinned columns at
 * either side, read from where those columns actually are. The pinned actions column is the one that
 * matters — a button over it would sit on top of the row's own controls.
 */
function visibleBand<TData extends MRT_RowData>(table: MRT_TableInstance<TData>, container: HTMLElement) {
  const visible = visibleEdges(container);
  const leftPinned = headerCellEdges(table, table.getLeftLeafHeaders());
  const rightPinned = headerCellEdges(table, table.getRightLeafHeaders());
  return {
    left: Math.max(visible.left, ...leftPinned.map((edge) => edge.right)),
    right: Math.min(visible.right, ...rightPinned.map((edge) => edge.left)),
  };
}

function tableEdges(container: HTMLElement): HorizontalEdges | null {
  const tableElement = container.querySelector("table");
  if (!tableElement) return null;
  const rect = tableElement.getBoundingClientRect();
  return { left: rect.left, right: rect.right };
}

function measure<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): ScrollButtonsLayout | null {
  const container = table.refs.tableContainerRef.current;
  const paper = table.refs.tablePaperRef.current;
  if (!container || !paper) return null;

  const paperRect = paper.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const band = visibleBand(table, container);
  // The middle of the part of the table that is on screen, not of the whole table: a tall table in a
  // page that scrolls would otherwise put its buttons below the fold.
  const visibleTop = Math.max(containerRect.top, 0);
  const visibleBottom = Math.min(containerRect.bottom, window.innerHeight || containerRect.bottom);
  const middle = visibleBottom > visibleTop ? (visibleTop + visibleBottom) / 2 : containerRect.top + containerRect.height / 2;

  return {
    paper,
    top: Math.round(middle - paperRect.top),
    left: Math.round(band.left - paperRect.left + EDGE_GAP),
    right: Math.round(paperRect.right - band.right + EDGE_GAP),
    canScroll: scrollableDirections(horizontalScrollState(container), tableEdges(container), visibleEdges(container)),
  };
}

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  transform: "translateY(-50%)",
  zIndex: ABOVE_THE_TABLE,
  width: 36,
  height: 36,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[3],
  color: theme.palette.text.primary,
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

export const SCROLL_BUTTON_LABELS: Record<ScrollDirection, string> = {
  left: "Scroll left",
  right: "Scroll right",
};

function prefersReducedMotion() {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type TableScrollButtonsProps<TData extends MRT_RowData> = { table: MRT_TableInstance<TData> };

/**
 * The buttons themselves, for the table `MRTNoMemo` draws. Nothing is drawn until the table has been
 * measured, so a renderer without `ResizeObserver` draws no buttons rather than guessing.
 */
export function TableScrollButtons<TData extends MRT_RowData>({ table }: Readonly<TableScrollButtonsProps<TData>>) {
  const [layout, setLayout] = useState<ScrollButtonsLayout | null>(null);
  const buttonRefs = useRef<Record<ScrollDirection, HTMLButtonElement | null>>({ left: null, right: null });
  /** The button last pressed, so focus can move to the other one when this one runs out of table. */
  const lastPressed = useRef<ScrollDirection | null>(null);

  useEffect(() => {
    const container = table.refs.tableContainerRef.current;
    const paper = table.refs.tablePaperRef.current;
    if (!container || !paper || typeof ResizeObserver === "undefined") return;

    const update = () => {
      const next = measure(table);
      setLayout((previous) => (sameLayout(previous, next) ? previous : next));
    };
    // The container and the table inside it each change size on their own: the pane narrows, or a
    // column is shown or hidden. Either can start or end the overflow. The observer reports once on
    // `observe`, which is what draws the buttons on mount.
    const observer = new ResizeObserver(update);
    observer.observe(container);
    observer.observe(paper);
    const tableElement = container.querySelector("table");
    if (tableElement) observer.observe(tableElement);
    // Capturing on the window hears the container's own scroll and any ancestor's, and an ancestor
    // scrolling moves which part of the table is on screen, which is what the buttons centre in.
    window.addEventListener("scroll", update, { capture: true, passive: true });
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, [table]);

  /**
   * A button that reaches its end disappears, and the focus it held would drop to the page body —
   * a keyboard reader would be sent back to the top of the document. It moves to the other button
   * instead, which is the one with somewhere left to go.
   */
  useLayoutEffect(() => {
    const pressed = lastPressed.current;
    if (!pressed || !layout || layout.canScroll[pressed]) return;
    lastPressed.current = null;
    const focusWasLost = document.activeElement === null || document.activeElement === document.body;
    if (focusWasLost) buttonRefs.current[pressed === "left" ? "right" : "left"]?.focus();
  }, [layout]);

  if (!layout) return null;

  const scroll = (direction: ScrollDirection) => {
    const container = table.refs.tableContainerRef.current;
    if (!container) return;
    lastPressed.current = direction;
    const distance = pageScrollDistance(
      direction,
      headerCellEdges(table, table.getCenterLeafHeaders()),
      visibleBand(table, container),
    );
    container.scrollBy({
      left: direction === "left" ? -distance : distance,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return createPortal(
    <>
      {layout.canScroll.left && (
        <ScrollButton
          ref={(node: HTMLButtonElement | null) => {
            buttonRefs.current.left = node;
          }}
          aria-label={SCROLL_BUTTON_LABELS.left}
          size="small"
          onClick={() => scroll("left")}
          style={{ top: layout.top, left: layout.left }}
        >
          <ChevronLeftIcon fontSize="small" />
        </ScrollButton>
      )}
      {layout.canScroll.right && (
        <ScrollButton
          ref={(node: HTMLButtonElement | null) => {
            buttonRefs.current.right = node;
          }}
          aria-label={SCROLL_BUTTON_LABELS.right}
          size="small"
          onClick={() => scroll("right")}
          style={{ top: layout.top, right: layout.right }}
        >
          <ChevronRightIcon fontSize="small" />
        </ScrollButton>
      )}
    </>,
    layout.paper,
  );
}
